import { observable, makeObservable, action } from "mobx";
import { ProjectManagementBasicDetails } from "../../../../models/ProjectManagement/ProjectManagementBasicDetails";
import { ProjectManagementFilters } from "../../../../models/ProjectManagement/ProjectManagementFilters";
import ProjectManagementAPI from "../../api/ProjectManagementAPI";
import { ProjectDetails } from "../../../../models/ProjectManagement/ProjectDetails";
import dayjs from "dayjs";
import { OpsSyncProjectCategory } from "../../../../models/ProjectManagement/OpsSyncProjectCategory";
import {
  ProjectRatingRequest,
  ProjectRatingResponse,
} from "../../../../models/OpsSync/OpsSyncFeedbackForm";
import { OpsSyncProjectProductProgress } from "../../../../models/ProjectManagement/OpssyncProjectProductProgress";
import { ModuleTasks } from "../../../../models/ModuleType";
import { Department } from "../../../../models/department/Department";
import {
  UnpaidSampleProduct,
  UnpaidSamplesReturnInfoUpdate,
  UnpaidSamplesReturnInfo,
} from "../../../../models/ProjectManagement/UnpaidSample";
import { UnpaidSampleFilters } from "../../../../models/ProjectManagement/UnpaidSampleFilters";
import { UnpaidSampleReturnDetailsResponse } from "../../../../models/ProjectManagement/UnpaidSample";
import {
  ProjectHandoverData,
  ProjectEmailerInfo,
  SendEmailerPopupInfo,
  ProjectEmailerType,
} from "../../../../models/ProjectManagement/ProjectProductsSourcingVariants";

export class ProjectManagementStore {
  projectManagementList: ProjectManagementBasicDetails[];
  projectManagementAPI: ProjectManagementAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  projectDetails: ProjectDetails | null;
  currentProjectDetails: ProjectDetails;
  opsSyncProjectCategories: OpsSyncProjectCategory[];
  opsSyncProjectTaskTypes: ModuleTasks[];
  opsSyncProjectProductProgress: OpsSyncProjectProductProgress[];
  departmentList: Department[];
  projectRatings: ProjectRatingResponse[];
  send_emailer_popup_info: SendEmailerPopupInfo | null;
  handoverCallInfo: ProjectHandoverData | null;
  emailerInfo: ProjectEmailerInfo | null;
  emailersInfo: ProjectEmailerInfo[] | null;
  refreshStates: {
    basic: boolean;
    information: boolean;
    ratings: boolean;
  };
  unpaidSampleResponse: UnpaidSampleReturnDetailsResponse | null;
  unpaidSamplePickupInfoResponce: UnpaidSamplesReturnInfo | null;
  unpaidSamplePickupUpdateInfoResponce: UnpaidSamplesReturnInfoUpdate | null;
  unpaidSamplePickupFormData: UnpaidSamplesReturnInfo;
  unpaidSamplePickupUpdateFormData: UnpaidSamplesReturnInfoUpdate;
  selectedReturnPickupId: number | null;
  selectedReturnProduct: UnpaidSampleProduct[];
  selectedReturnProductOrderId: number;
  unpaidSamplePickupSelectedFiles: File[];
  unpaidSamplePickupReceivedAtOffice: boolean;

  constructor() {
    this.projectManagementList = [];
    this.projectManagementAPI = new ProjectManagementAPI();
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.projectDetails = null;
    this.currentProjectDetails = {} as ProjectDetails;
    this.opsSyncProjectCategories = [];
    this.opsSyncProjectTaskTypes = [];
    this.opsSyncProjectProductProgress = [];
    this.departmentList = [];
    this.projectRatings = [];
    this.handoverCallInfo = null;
    this.emailerInfo = null;
    this.emailersInfo = null;
    this.send_emailer_popup_info = null;
    this.refreshStates = {
      basic: false,
      information: false,
      ratings: false,
    };
    this.unpaidSampleResponse = null;
    this.unpaidSamplePickupInfoResponce = null;
    this.unpaidSamplePickupUpdateInfoResponce = null;
    this.unpaidSamplePickupFormData = {} as UnpaidSamplesReturnInfo;
    this.unpaidSamplePickupUpdateFormData = {} as UnpaidSamplesReturnInfoUpdate;
    this.selectedReturnPickupId = null;
    this.selectedReturnProduct = [];
    this.selectedReturnProductOrderId = 0;
    this.unpaidSamplePickupSelectedFiles = [];
    this.unpaidSamplePickupReceivedAtOffice = false;

    makeObservable(this, {
      projectManagementList: observable,
      projectDetails: observable,
      currentProjectDetails: observable,
      refreshStates: observable,
      opsSyncProjectCategories: observable,
      opsSyncProjectTaskTypes: observable,
      opsSyncProjectProductProgress: observable,
      departmentList: observable,
      unpaidSampleResponse: observable,
      unpaidSamplePickupInfoResponce: observable,
      unpaidSamplePickupUpdateInfoResponce: observable,
      unpaidSamplePickupFormData: observable,
      unpaidSamplePickupUpdateFormData: observable,
      selectedReturnPickupId: observable,
      selectedReturnProduct: observable,
      selectedReturnProductOrderId: observable,
      unpaidSamplePickupSelectedFiles: observable,
      unpaidSamplePickupReceivedAtOffice: observable,
      updateCurrentProjectDetails: action,
      saveProjectDetails: action,
      getProjectManagementList: action,
      getProjectDetails: action,
      resetProjectDetails: action,
      sendNpsEmail: action,
      updateProjectNotes: action,
      updateCurrentProjectDetailsBatch: action,
      updateExcludeFromSourcing: action,
      updateExcludeFromProjects: action,
      getOpsSyncProjectCategories: action,
      getOpsSyncProjectTaskTypes: action,
      getOpsSyncProjectProductProgress: action,
      getProjectRatings: action,
      getDepartmentList: action,
      setRefreshState: action,
      refreshBasicDetails: action,
      refreshProductDetails: action,
      refreshRatings: action,
      getUnpaidSampleReturnListing: action,
      getUnpaidSampleReturnInfoById: action,
      getUnpaidSampleReturnDetailsById: action,
      updateUnpaidSamplePickupDate: action,
      updateUnpaidSamplePickupInfoUpdate: action,
      updateSampleReturnInfoResponse: action,
      setselectedReturnProduct: action,
      updateSelectedReturnProduct: action,
      selectAllselectedReturnProduct: action,
      deleteUnpaidSampleProductImage: action,
      setUnpaidSamplePickupSelectedFiles: action,
      addUnpaidSamplePickupFiles: action,
      removeUnpaidSamplePickupFileAtIndex: action,
      setUnpaidSamplePickupReceivedAtOffice: action,
      fetchEmailerInfo: action,
      fetchEmailersInfo: action,
      sendPendingEmailerEmail: action,
    });
  }

  getProjectManagementList = async (params: ProjectManagementFilters) => {
    try {
      const response =
        await this.projectManagementAPI.getProjectManagementList(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.projectManagementList = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getOpsSyncProjectList = async (params: ProjectManagementFilters) => {
    try {
      const response =
        await this.projectManagementAPI.getOpsSyncProjectList(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.projectManagementList = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectDetails = async (projectId: number): Promise<void> => {
    try {
      const response =
        await this.projectManagementAPI.getProjectDetails(projectId);

      const formattedResponse = {
        ...response,
        delivery_date: response.delivery_date
          ? dayjs.utc(response.delivery_date).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
        dispatch_date: response.dispatch_date
          ? dayjs.utc(response.dispatch_date).format("YYYY-MM-DD HH:mm:ss")
          : undefined,
      };

      this.updateCurrentProjectDetailsBatch(formattedResponse);

      this.projectDetails = formattedResponse;
    } catch (error: any) {
      throw error;
    }
  };

  updateCurrentProjectDetailsBatch = (
    updatedDetails: Partial<ProjectDetails>,
  ) => {
    this.currentProjectDetails = {
      ...this.currentProjectDetails,
      ...updatedDetails,
    };
  };

  updateCurrentProjectDetails = <K extends keyof ProjectDetails>(
    key: K,
    value: ProjectDetails[K],
  ) => {
    this.currentProjectDetails = {
      ...this.currentProjectDetails,
      [key]: value,
    };
  };

  saveProjectDetails = async () => {
    try {
      const response = await this.projectManagementAPI.saveProjectDetails(
        this.currentProjectDetails,
      );

      if (response) {
        this.currentProjectDetails = {
          ...this.currentProjectDetails,
          progress: response.progress!,
          module_wise_progress: this.currentProjectDetails?.module_wise_progress
            ? this.currentProjectDetails.module_wise_progress.map(
                (module, index) =>
                  index === 0
                    ? { ...module, progress: response.progress! }
                    : module,
              )
            : [],
        };
      }
    } catch (error: any) {
      throw error;
    }
  };

  resetProjectDetails = async () => {
    this.currentProjectDetails = this.projectDetails
      ? this.projectDetails
      : ({} as ProjectDetails);
  };

  sendNpsEmail = async (name: string, email: string, orderId: number) => {
    try {
      const response = await this.projectManagementAPI.sendNpsEmail(
        name,
        email,
        orderId,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  sendCsatEmail = async (orderId: number) => {
    try {
      const response = await this.projectManagementAPI.sendCsatEmail(orderId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateProjectNotes = (value: string) => {
    this.currentProjectDetails = {
      ...this.currentProjectDetails,
      order_additional_info: value,
    };
  };

  updateExcludeFromProjects = (value: boolean, reason: string | null) => {
    this.currentProjectDetails = {
      ...this.currentProjectDetails,
      excluded_from_projects: value,
      reason_to_exclude_from_projects: reason,
      excluded_from_projects_by_name: null,
      excluded_from_project_date: null,
    };
  };

  updateExcludeFromSourcing = (value: boolean, reason: string | null) => {
    this.currentProjectDetails = {
      ...this.currentProjectDetails,
      excluded_from_sourcing: value,
      reason_to_exclude_from_sourcing: reason,
      excluded_from_sourcing_by_name: null,
      excluded_from_sourcing_date: null,
    };
  };

  // ops-sync
  getOpsSyncProjectCategories = async () => {
    try {
      const response =
        await this.projectManagementAPI.getOpsSyncProjectCategories();
      this.opsSyncProjectCategories = response;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectRatings = async (projectId: number) => {
    try {
      const response =
        await this.projectManagementAPI.getProjectRatings(projectId);
      this.projectRatings = response;
    } catch (error: any) {
      throw error;
    }
  };

  getOpsSyncProjectTaskTypes = async () => {
    try {
      const response =
        await this.projectManagementAPI.getOpsSyncProjectTaskTypes();
      this.opsSyncProjectTaskTypes = response;
    } catch (error: any) {
      throw error;
    }
  };

  getOpsSyncProjectProductProgress = async (projectId: number) => {
    try {
      const response =
        await this.projectManagementAPI.getOpsSyncProjectProductProgress(
          projectId,
        );
      this.opsSyncProjectProductProgress = response;
    } catch (error: any) {
      throw error;
    }
  };

  getDepartmentList = async () => {
    try {
      const response = await this.projectManagementAPI.getDepartmentList();
      this.departmentList = response;
    } catch (error: any) {
      throw error;
    }
  };

  setRefreshState = (type: keyof typeof this.refreshStates, value: boolean) => {
    this.refreshStates[type] = value;
  };

  refreshBasicDetails = async (orderId: number) => {
    if (!orderId) return;

    this.setRefreshState("basic", true);

    try {
      await this.getProjectDetails(orderId);
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.setRefreshState("basic", false);
      }, 500);
    }
  };

  refreshProductDetails = async (orderId: number) => {
    if (!orderId) return;

    this.setRefreshState("information", true);

    try {
      await this.getOpsSyncProjectProductProgress(orderId);
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.setRefreshState("information", false);
      }, 500);
    }
  };

  refreshRatings = async (orderId: number) => {
    if (!orderId) return;
    this.setRefreshState("ratings", true);
    try {
      await this.getProjectRatings(orderId);
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.setRefreshState("ratings", false);
      }, 500);
    }
  };

  saveProjectRating = async (
    projectId: number,
    payload: ProjectRatingRequest,
  ) => {
    try {
      const response = await this.projectManagementAPI.saveProjectRating(
        projectId,
        payload,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getRatingById = async (ratingId: number) => {
    try {
      const response = await this.projectManagementAPI.getRatingById(ratingId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateProjectRating = async (
    ratingId: number,
    payload: ProjectRatingRequest,
  ) => {
    try {
      const response = await this.projectManagementAPI.updateProjectRating(
        ratingId,
        payload,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  makeArchived = async (orderId: number, reason?: string) => {
    try {
      const response = await this.projectManagementAPI.makeArchived(
        orderId,
        reason,
      );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  validateArchive = async (orderId: number) => {
    try {
      const response = await this.projectManagementAPI.validateArchive(orderId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  makeUnarchived = async (orderId: number) => {
    try {
      const response = await this.projectManagementAPI.makeUnarchived(orderId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  fetchHandoverCallInfo = async (projectId: number): Promise<void> => {
    try {
      const response =
        await this.projectManagementAPI.fetchHandoverCallInfo(projectId);

      this.handoverCallInfo = response;
      if (!response.expected_dispatch_date) {
        this.handoverCallInfo!.expected_dispatch_date =
          this.projectDetails!.dispatch_date;
      }
      return;
    } catch (error: any) {
      throw error;
    }
  };

  saveHandoverCallInfo = async (
    projectId: number,
    handoverCallInfo: ProjectHandoverData,
  ): Promise<void> => {
    try {
      const existing_id = this.handoverCallInfo?.id;
      this.handoverCallInfo = { ...handoverCallInfo };
      this.handoverCallInfo!.id = existing_id;

      if (existing_id === undefined) {
        const id = await this.projectManagementAPI.addHandoverCallInfo(
          projectId,
          this.handoverCallInfo!,
        );
        this.handoverCallInfo!.id = id;
      } else {
        await this.projectManagementAPI.updateHandoverCallInfo(
          projectId,
          this.handoverCallInfo!,
        );
      }
      return;
    } catch (error: any) {
      throw error;
    }
  };

  sendHandoverCallInfoEmail = async (
    projectId: number,
    preview_email: boolean,
  ): Promise<void> => {
    try {
      const response =
        await this.projectManagementAPI.sendHandoverCallInfoEmail(
          projectId,
          preview_email,
        );
      this.handoverCallInfo = response;
      return;
    } catch (error: any) {
      throw error;
    }
  };

  fetchEmailerInfo = async (
    projectId: number,
    type: ProjectEmailerType,
  ): Promise<void> => {
    try {
      const response = await this.projectManagementAPI.fetchPendingEmailerInfo(
        projectId,
        type,
      );
      this.emailerInfo = response;
      return;
    } catch (error: any) {
      this.emailerInfo = null;
      throw error;
    }
  };

  fetchEmailersInfo = async (projectId: number): Promise<void> => {
    try {
      const response =
        await this.projectManagementAPI.fetchEmailersInfo(projectId);
      this.emailersInfo = response;
    } catch (error: any) {
      this.emailersInfo = [];
      throw error;
    }
  };

  sendPendingEmailerEmail = async (
    projectId: number,
    type: ProjectEmailerType,
    preview_email: boolean,
  ): Promise<void> => {
    try {
      const response = await this.projectManagementAPI.sendPendingEmailerEmail(
        projectId,
        type,
        preview_email,
      );
      this.emailerInfo = response;
      return;
    } catch (error: any) {
      throw error;
    }
  };

  updateUnpaidSamplePickupDate = async (
    order_id: string,
    pickup_info: UnpaidSamplesReturnInfo,
  ) => {
    try {
      const response =
        await this.projectManagementAPI.updateUnpaidSamplePickupDate(
          order_id,
          pickup_info,
        );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateSampleReturnInfoResponse = (response: UnpaidSamplesReturnInfo) => {
    if (this.unpaidSamplePickupInfoResponce) {
      const image_urls =
        response.image_urls ??
        (response.image_url
          ? response.image_url.map((url, i) => ({
              id: i,
              image_url: url,
            }))
          : undefined);

      this.unpaidSamplePickupInfoResponce = {
        ...this.unpaidSamplePickupInfoResponce,
        ...response,
        image_url: response.image_url,
        image_urls:
          image_urls ?? this.unpaidSamplePickupInfoResponce.image_urls,
        sample_received_at_office: response.sample_received_at_office,
      };
    }
  };

  getUnpaidSampleReturnListing = async (params: UnpaidSampleFilters) => {
    try {
      const response =
        await this.projectManagementAPI.getUnpaidSampleReturnListing(params);
      this.unpaidSampleResponse = response.result;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateUnpaidSamplePickupInfoUpdate = async (
    order_id: number,
    pickup_id: number,
    payload: UnpaidSamplesReturnInfoUpdate,
  ) => {
    try {
      const response =
        await this.projectManagementAPI.updateUnpaidSamplePickupInfoUpdate(
          order_id,
          pickup_id,
          payload,
        );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getUnpaidSampleReturnInfoById = async (order_id: number) => {
    try {
      const response =
        await this.projectManagementAPI.getUnpaidSampleReturnInfoById(order_id);
      this.unpaidSamplePickupInfoResponce = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getUnpaidSampleReturnDetailsById = async (
    order_id: number,
    pickup_id: number,
  ) => {
    try {
      const response =
        await this.projectManagementAPI.getUnpaidSampleReturnDetailsById(
          order_id,
          pickup_id,
        );
      this.unpaidSamplePickupUpdateInfoResponce = response;
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  setUnpaidSamplePickupFormData = (data: UnpaidSamplesReturnInfo) => {
    this.unpaidSamplePickupFormData = data;
  };

  updateUnpaidSamplePickupFormData = (
    updates: Partial<UnpaidSamplesReturnInfo>,
  ) => {
    this.unpaidSamplePickupFormData = {
      ...this.unpaidSamplePickupFormData,
      ...updates,
    };
  };

  resetUnpaidSamplePickupFormData = () => {
    this.unpaidSamplePickupFormData = {} as UnpaidSamplesReturnInfo;
    this.unpaidSamplePickupSelectedFiles = [];
    this.unpaidSamplePickupReceivedAtOffice = false;
  };

  setUnpaidSamplePickupSelectedFiles = (files: File[]) => {
    this.unpaidSamplePickupSelectedFiles = files;
  };

  addUnpaidSamplePickupFiles = (files: File[]) => {
    this.unpaidSamplePickupSelectedFiles = [
      ...this.unpaidSamplePickupSelectedFiles,
      ...files,
    ];
  };

  removeUnpaidSamplePickupFileAtIndex = (index: number) => {
    this.unpaidSamplePickupSelectedFiles =
      this.unpaidSamplePickupSelectedFiles.filter((_, i) => i !== index);
  };

  setUnpaidSamplePickupReceivedAtOffice = (value: boolean) => {
    this.unpaidSamplePickupReceivedAtOffice = value;
    this.unpaidSamplePickupFormData = {
      ...this.unpaidSamplePickupFormData,
      sample_received_at_office: value,
    };
    if (!value) {
      this.unpaidSamplePickupSelectedFiles = [];
    }
  };

  setSelectedReturnProductOrderId = (orderId: number) => {
    this.selectedReturnProductOrderId = orderId;
  };

  setUnpaidSamplePickupUpdateFormData = (
    data: UnpaidSamplesReturnInfoUpdate,
  ) => {
    this.unpaidSamplePickupUpdateFormData = data;
  };

  resetUnpaidSamplePickupUpdateFormData = () => {
    this.unpaidSamplePickupUpdateFormData = {} as UnpaidSamplesReturnInfoUpdate;
  };

  setselectedReturnPickupId = (pickupId: number) => {
    this.selectedReturnPickupId = pickupId;
  };

  setselectedReturnProduct = (products: UnpaidSampleProduct[]) => {
    this.selectedReturnProduct = products;
  };

  updateSelectedReturnProduct = (
    id: number,
    updates: Partial<UnpaidSampleProduct>,
  ) => {
    const index = this.selectedReturnProduct.findIndex((p) => p.id === id);
    if (index !== -1) {
      this.selectedReturnProduct[index] = {
        ...this.selectedReturnProduct[index],
        ...updates,
      };
    }
  };

  selectAllselectedReturnProduct = (selected: boolean) => {
    this.selectedReturnProduct = this.selectedReturnProduct.map((product) => ({
      ...product,
      is_selected: selected,
    }));
  };

  deleteUnpaidSampleProductImage = async (
    pickup_id: number,
    image_url: string,
    id: number,
  ) => {
    try {
      const response =
        await this.projectManagementAPI.deleteUnpaidSampleProductImage(
          pickup_id,
          image_url,
          id,
        );
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  updateBackfillAllowed = async (
    projectId: number,
    backfillAllowed: boolean,
  ) => {
    try {
      await this.projectManagementAPI.updateBackfillAllowed(
        projectId,
        backfillAllowed,
      );
      this.currentProjectDetails = {
        ...this.currentProjectDetails,
        backfill_allowed: backfillAllowed,
      };
    } catch (error) {
      this.currentProjectDetails = {
        ...this.currentProjectDetails,
        backfill_allowed: !backfillAllowed,
      };
      throw error;
    }
  };
}
